import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'
import { IQuestionV2, MainObject } from '../../store/reports/types'
import FormLayout from '../forms/FormLayout'
import { PhotoCamera } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import selectOptions from '../../constants/selectOptions'
import LabelErrorTAG from '../LabelErrorTAG'

import { ReactComponent as ArrowIcon } from '../../assets/images/arrowIcon.svg'
import { selectStyle, textAreaStyle } from '../../constants/styleConstants'
import {
  addNewVictimForm,
  removeVictimForm,
  setVictimValues,
} from '../../store/questions/questionsSlice'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../store'
import questionsSelector from '../../store/questions/questionsSelector'
import DividerTAG from '../DividerTAG'
import { questionParser, transformStructure } from '.'
import questionsService from '../../services/questionsService'
import metricUtils from '../../pages/MetricsPage/utils'

import RemoveButton from '../RemoveButton'
import reporterConstants from '../../components/forms/ReportingPartyForm/constants'
import colors from '../../constants/colors'
import { renderSelectOptions } from '../CaseOverview/utils'

const imageUploadRowStyle = {
  height: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '30vh',
  maxHeight: '30vh',
  width: '30vh',
}

const imageMessageStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  border: '1px solid #ccc',
  borderRadius: '10px',
}

interface IVictimFormQuestion {
  question: IQuestionV2
  victimQuestionId?: number | null
}

interface IVictimQuestions {
  victimUuid: number
  victimAnswers: MainObject[]
}

const VictimFormQuestion: React.FC<IVictimFormQuestion> = ({
  question,
  victimQuestionId = null,
}) => {
  const getAnswer = () => {
    return useSelector(questionsSelector.answerToNewCaseQuestion(question?.uuid))
  }
  const answer = getAnswer()
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [defaultVictimQuestions, setDefaultVictimQuestions] = useState<IQuestionV2[]>([])
  const [victimQuestions, setVictimsQuestions] = useState<IVictimQuestions[]>([])
  const victimsData = useSelector(questionsSelector.newCaseVictims)

  const dispatch = useDispatch<AppDispatch>()

  // const handleFileChange = (e: ChangeEvent<HTMLInputElement>, victimId: number) => {
  //   if (e?.target?.files) {
  //     const file = e?.target?.files[0]
  //     file.arrayBuffer().then(arrayBuffer => {
  //       const blob = new Blob([new Uint8Array(arrayBuffer)], { type: file.type })
  //       dispatch(setVictimValues({ field: e.target.name, value: blob, victimId }))
  //     })
  //     setImagePreview(URL.createObjectURL(file))
  //   }
  // }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>, victimId: number) => {
    dispatch(setVictimValues({ field: e.target.name, value: e.target.value, victimId }))
  }

  const onChangeHandler = (e: SelectChangeEvent, victimId: number) => {
    dispatch(setVictimValues({ field: e.target.name, value: e.target.value, victimId }))
  }

  const onChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>, victimId: number) => {
    dispatch(setVictimValues({ field: e.target.name, value: e.target.value, victimId }))
  }

  const addVictimHandler = () => {
    const transformedStructures = defaultVictimQuestions.map(transformStructure) as MainObject[]
    const victimId = metricUtils.getRandomInt()
    const newObject = {
      victimUuid: victimId,
      victimAnswers: transformedStructures,
    } as IVictimQuestions
    setVictimsQuestions(prev => {
      if (!prev || prev?.length == 0) {
        return [newObject]
      }
      const found = prev.find(obj => obj.victimUuid === newObject.victimUuid)
      if (!found) {
        return [...prev, newObject]
      }
      return prev
    })
    dispatch(
      addNewVictimForm({
        victimId,
        answers: newObject.victimAnswers,
        defaultAnswers: transformedStructures,
      }),
    )
  }

  const removeHandler = (victimId: number) => {
    dispatch(removeVictimForm({ victimId }))
  }

  const resolveVictimQuestions = (victimId: number) => {
    if (victimQuestions.length > 0 && defaultVictimQuestions.length > 0) {
      const victimData = victimQuestions.find(victim => victim?.victimUuid === victimId)
      if (!victimData) {
        return <></>
      }

      return defaultVictimQuestions.map(victimQuestion =>
        questionParser(victimQuestion, false, victimId),
      )
    }
    return <></>
  }

  const renderVictimsQuestions = (victim: any) => {
    return reporterConstants.textFieldBasedOnSubjectType[victim.person_type].map(field => {
      return (
        <Stack key={field.fieldName}>
          <LabelErrorTAG label={field.label} />
          <TextField
            value={victim?.[field.fieldName]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(e, victim?.victimId)
            }}
            variant='standard'
            type={'text'}
            fullWidth
            id={field.fieldName}
            name={field.fieldName}
          />
        </Stack>
      )
    })
  }

  const renderVictimsForms = () => {
    return victimsData.map(victim => {
      return (
        <>
          {victim.person_type == '' ? (
            <Box sx={{ width: '100%', marginTop: '1.8rem' }} key={`${victim?.victimId}-selectType`}>
              {' '}
              <Select
                onChange={e => {
                  onChangeHandler(e, victim?.victimId)
                }}
                value={victim?.person_type}
                IconComponent={ArrowIcon}
                displayEmpty
                renderValue={
                  victim?.person_type !== ''
                    ? undefined
                    : () => (
                        <Typography variant='bodyRegular' color={colors.TAG_TEXT_GRAY}>
                          {'Select a value'}
                        </Typography>
                      )
                }
                id='person_type'
                name='person_type'
                sx={selectStyle}
              >
                {renderSelectOptions(selectOptions.subjectTypes)}
              </Select>
            </Box>
          ) : (
            <Grid container xs={12} spacing={2}>
              <Grid item xs={12}>
                {' '}
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'end' }}>
                  <RemoveButton
                    removeFunctionHandler={() => {
                      removeHandler(victim?.victimId)
                    }}
                    tooltipMessage='Remove Victim'
                  />
                </Box>
                ,
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* <Stack sx={imageUploadRowStyle}>
                  {!victim?.image && (
                    <Box sx={imageMessageStyle}>
                      <Typography sx={{ textAlign: 'center' }}>Upload Profile Image</Typography>
                    </Box>
                  )}
                  {victim?.image && (
                    <Box sx={{ width: { xs: '100%' }, height: { xs: '100%' } }}>
                      <img
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        src={URL.createObjectURL(victim?.image as unknown as Blob)}
                      />
                    </Box>
                  )}
                  <Button
                    variant='submitFormButton'
                    component='label'
                    sx={{ mt: '18px' }}
                    endIcon={<PhotoCamera />}
                  >
                    <Typography variant='bodyBold'>
                      {imagePreview ? 'Change Image' : 'Upload Image'}
                    </Typography>
                    <input
                      onChange={e => {
                        handleFileChange(e, victim?.victimId)
                      }}
                      hidden
                      multiple
                      accept='image/*'
                      type='file'
                      id='image'
                      name='image'
                    />
                  </Button>
                </Stack> */}
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormLayout>
                  <Select
                    onChange={(e: SelectChangeEvent) => {
                      onChangeHandler(e, victim?.victimId)
                    }}
                    value={victim?.person_type as string}
                    IconComponent={ArrowIcon}
                    displayEmpty
                    sx={[selectStyle]}
                    id='person_type'
                    name='person_type'
                  >
                    {selectOptions.subjectTypes.map(option => {
                      return (
                        <MenuItem value={option?.value} key={option?.value}>
                          <Typography variant='bodyBold'>{option?.label}</Typography>
                        </MenuItem>
                      )
                    })}
                  </Select>
                  {renderVictimsQuestions(victim)}
                  <Stack>
                    <LabelErrorTAG label='Description' />
                    <TextareaAutosize
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        onChangeTextArea(e, victim?.victimId)
                      }}
                      value={victim?.description}
                      minRows={5}
                      style={textAreaStyle}
                      id='description'
                      name='description'
                      placeholder='Write a brief message'
                    />
                  </Stack>
                  {resolveVictimQuestions(victim?.victimId)}
                </FormLayout>
              </Grid>
              <DividerTAG sx={{ my: '2rem' }} />
            </Grid>
          )}
        </>
      )
    })
  }

  const getVictimsQuestions = async () => {
    const { data } = await questionsService.getVictimsQuestions()
    setDefaultVictimQuestions(data)
  }
  useEffect(() => {
    getVictimsQuestions()
  }, [])

  return (
    <>
      {' '}
      <>
        <FormLayout>
          <Button startIcon={<AddIcon />} variant='submitFormButton' onClick={addVictimHandler}>
            <Typography variant='bodyBold'>Add Person</Typography>
          </Button>
          <DividerTAG />
        </FormLayout>
        {victimsData && renderVictimsForms()}
      </>
    </>
  )
}

export default VictimFormQuestion
